import dayjs from "dayjs";
import { PhoneInput, ProfileForm } from "./profile";

type Birthplace = {
  city?: string;
  country?: string;
  departmentNumber?: string;
  nationality?: string;
};

type Address = {
  addressDetails: string;
  additionalAddress?: string;
  zipCode: string;
  city: string;
  country: string;
  addressType: string;
};

export enum PhoneType {
  MOBILE_PHONE = "MobilePhone",
  FIXED_LINE = "FixedLine",
}

type Phone = {
  id?: string;
  phoneType: PhoneType;
  phoneNumber: string;
  countryCode: number;
  countryCodeName: string;
};

type Contact = {
  label?: string;
  mail?: string;
  phones?: Phone[];
  address?: Address;
};

export type File = {
  id: string;
  name: string;
  url: string;
};

type IdentityPiece = {
  identityPieceNumber?: string;
  deliveryDate?: string;
  obtainingPlace?: string;
  endValidityDate?: string;
  identityPieceType?: string;
  country?: string;
  lastname?: string;
  firstname?: string;
  birthdate?: string;
  birthplaceCity?: string;
  birthplaceZipCode?: string;
  birthplaceCountry?: string;
  id?: string;
  referenceDocumentId?: string | null;
  documentFiles?: File[];
};

type DrivingLicense = {
  drivingLicenseNationality?: string;
  drivingLicenseNumber?: string;
  deliveryDate?: string | null;
  obtainingPlace?: string;
  endValidityDate: string;
  lastname: string;
  firstname: string;
  country: string;
  birthdate: string;
  birthplaceCity: string;
  birthplaceZipCode: string;
  birthplaceCountry: string;
  id?: string;
  referenceDocumentId?: string | null;
  documentFiles?: File[];
};

type Proof = {
  proofType: string;
  proofDate: string;
  name: string;
  documentFiles: File[];
  referenceDocumentId: string;
};

type Documents = {
  identityPiece: IdentityPiece | null;
  drivingLicense: DrivingLicense | null;
  domicileProof?: Proof;
  legalProof?: Proof;
};

export type UserDetails = {
  civility?: string;
  firstname: string;
  lastname: string;
  birthdate?: string;
  birthPlace?: Birthplace;
  contacts?: Contact[];
  documents: Documents | null;
};

const webLabel = "WEB";

export const getCivility = (userData: UserDetails) => {
  return userData.civility === "Mr"
    ? "Monsieur"
    : userData.civility === "Mrs"
    ? "Madame"
    : undefined;
};

export const getBirthdate = (userData: UserDetails) => {
  return userData.birthdate !== undefined
    ? dayjs(userData.birthdate).format("DD/MM/YYYY")
    : null;
};

export const getlicenseDate = (userData: UserDetails) => {
  if (userData.documents?.drivingLicense !== null) {
    return dayjs(userData.documents?.drivingLicense.deliveryDate).format(
      "DD/MM/YYYY"
    );
  } else {
    return null;
  }
};

export const getAddress = (userData: UserDetails) =>
  userData.contacts?.find((x) => x.label === webLabel)?.address ?? null;

export const getEmail = (userData: UserDetails) =>
  userData.contacts?.find((x) => x.label === webLabel)?.mail ?? null;

export const getPhone = (userData: UserDetails, phoneType: PhoneType) => {
  const phones = userData.contacts?.find((x) => x.label === webLabel)?.phones;
  if (phones) {
    if (phones.length) {
      const phone = phones.find((value) => value.phoneType === phoneType);

      if (phone) {
        return {
          id: phone.id,
          phoneType: phone.phoneType,
          phoneNumber: phone.phoneNumber,
          countryCode: phone.countryCode,
          countryCodeName: phone.countryCodeName,
          isValid: true,
        };
      }
    }
  }

  return {
    phoneType: phoneType,
    phoneNumber: "",
    countryCode: 0,
    countryCodeName: "",
    isValid: true,
  };
};

const setCivility = (field?: string) => {
  return field === "Monsieur" ? "Mr" : field === "Madame" ? "Mrs" : "";
};

/*
const setBirthdate = (field?: string | null) => {
  return field !== null && field !== undefined
    ? dayjs(field, "DD/MM/YYYY").add(1, "hour").toJSON()
    : "";
};

const setLicenseDate = (field?: string | null) => {
  return field !== null && field !== undefined
    ? dayjs(field, "DD/MM/YYYY").add(1, "hour").toJSON()
    : "";
};

*/

const removePhoneFromPhonesList = (phones: Phone[], formPhone: PhoneInput) =>
  phones.filter((x) => x.id !== formPhone.id);

const addOrUpdatePhone = (phones: Phone[], formPhone: PhoneInput) => {
  if (!formPhone.phoneNumber) {
    return phones;
  }

  if (formPhone.id != null) {
    phones = removePhoneFromPhonesList(phones, formPhone);
  }

  return [formPhone, ...phones];
};

const setPhones = (phones: Phone[] | undefined, form: ProfileForm): Phone[] => {
  let updatedPhones = phones ?? [];
  updatedPhones = addOrUpdatePhone(updatedPhones, form.values.mobile);
  updatedPhones = addOrUpdatePhone(updatedPhones, form.values.phone);

  return updatedPhones;
};

const setContacts = (
  contacts: Contact[] | undefined,
  form: ProfileForm
): Contact[] => {
  const defaultWebContact: Contact = {
    label: webLabel,
    mail: form.values.email,
    phones: [],
  };

  if (!contacts) {
    contacts = [];
  }

  const contactsWithoutWebContact = contacts.filter(
    (x) => x.label !== webLabel
  );
  const webContact =
    contacts.find((x) => x.label === webLabel) ?? defaultWebContact;

  webContact.phones = setPhones(webContact.phones, form);

  return [webContact, ...contactsWithoutWebContact];
};

export const updateUserData = (userData: UserDetails, form: ProfileForm) => {
  const updatedUserData = { ...userData };
  updatedUserData.civility = setCivility(form.values.civility);
  updatedUserData.lastname = form.values.lastname;
  updatedUserData.firstname = form.values.firstname;

  updatedUserData.contacts = setContacts(updatedUserData.contacts, form);

  /*  
  updatedUserData.contacts.phones = [];
  if (form.values.mobile.phoneNumber) {
    updatedUserData.phones.push(setPhone(form.values.mobile, "MobilePhone"));
  }

  if (form.values.phone.phoneNumber) {
    updatedUserData.phones.push(setPhone(form.values.phone, "FixedPhone"));
  }

  updatedUserData.addresses = [];
  if (
    form.values.street !== undefined &&
    form.values.zipCode !== undefined &&
    form.values.city !== undefined &&
    form.values.country !== undefined
  ) {
    updatedUserData.addresses.push({
      addressDetails: form.values.street,
      additionalAddress: form.values.additional,
      zipCode: form.values.zipCode,
      city: form.values.city,
      country: form.values.country,
      addressType: "Domicile",
    });
  }

  updatedUserData.birthDate = setBirthdate(form.values.birthDate);
  updatedUserData.birthPlace = {
    city: form.values.birthCity,
    country: form.values.birthCountry,
    departmentNumber: form.values.birthDepartmentNumber,
    nationality: form.values.nationality,
  };

  if (updatedUserData.documents?.drivingLicenses) {
    updatedUserData.documents.drivingLicenses.deliveryDate = setLicenseDate(
      form.values.licenseDate
    );
    updatedUserData.documents.drivingLicenses.obtainingPlace =
      form.values.licenseCity;
    updatedUserData.documents.drivingLicenses.drivingLicenseNumber =
      form.values.licenseId;
    updatedUserData.documents.drivingLicenses.drivingLicenseNationality =
      form.values.licenseCountry;
  }*/

  return updatedUserData;
};
